import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { FlaskConical, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const redirect = location.state?.from || "/dashboard";

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const u = await login(username, password);
      toast.success("Welcome back!");
      navigate(u.role === "admin" ? "/admin" : redirect);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6 py-12" data-testid="login-page">
      <div className="w-full max-w-md">
        <Link to="/" className="mx-auto flex w-fit items-center gap-2.5">
          <span className="grid h-9 w-9 place-items-center rounded-xl bg-ocean"><FlaskConical className="h-5 w-5 text-teal-light" /></span>
          <span className="font-heading text-lg font-bold"><span className="text-ocean">Verify</span><span className="text-teal">MySupps</span></span>
        </Link>
        <div className="mt-8 rounded-3xl border border-border bg-white p-8 shadow-[0_8px_30px_rgba(10,61,98,0.06)]">
          <h1 className="text-2xl font-bold text-ocean">Log in</h1>
          <p className="mt-1 text-sm text-slate-500">Access your dashboard and results.</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Username</label>
              <input data-testid="login-username" value={username} onChange={(e) => setUsername(e.target.value)} required
                className="mt-1.5 w-full rounded-xl border border-border bg-white px-4 py-3 text-sm outline-none focus:border-teal focus:ring-2 focus:ring-teal/20" placeholder="Your username" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Password</label>
              <input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                className="mt-1.5 w-full rounded-xl border border-border bg-white px-4 py-3 text-sm outline-none focus:border-teal focus:ring-2 focus:ring-teal/20" placeholder="••••••••" />
            </div>
            {error && <p className="text-sm text-red-500" data-testid="login-error">{error}</p>}
            <button type="submit" disabled={loading} data-testid="login-submit"
              className="flex w-full items-center justify-center gap-2 rounded-full bg-ocean px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-ocean-light disabled:opacity-60">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />} Log in
            </button>
          </form>
          <p className="mt-6 text-center text-sm text-slate-500">
            No account? <Link to="/register" className="font-semibold text-teal hover:text-teal-dark">Create one</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
